import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Head from 'react-helmet';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import numeral from 'numeral';

import Menu from 'components/menu';
import Box from 'components/box';
import { Alert } from 'components/alerts';
import LoadingIndicator from 'components/loading-indicator';
import EnrollmentPlanFormModal from 'components/branch/enrollment-plan-form-modal';
import api from 'lib/api';
import { userSelector } from 'store/auth/auth-selectors';

/* ------------------------------------------------------------------ *
 * Layout
 * ------------------------------------------------------------------ */
const Page = styled.main`
  width: 100%;
  min-height: calc(100vh - ${props => props.theme.layout.menuHeight}px);
  background: linear-gradient(180deg, #f7f8fb 0%, #eef1f6 100%);
`;

const Content = styled.div`
  max-width: 1240px;
  margin: 0 auto;
  padding: 28px 40px 56px;

  @media (max-width: 720px) {
    padding: 20px 16px 40px;
  }
`;

const HeaderCard = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  background: #fff;
  border: 1px solid #e7eaf0;
  border-radius: 16px;
  padding: 22px 26px;
  margin-bottom: 24px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #101828;
`;

const HeaderSubtitle = styled.div`
  margin-top: 6px;
  font-size: 14px;
  color: #667085;
`;

const PrimaryBtn = styled.button`
  appearance: none;
  border: 1px solid transparent;
  background: #3b6cf6;
  color: #fff;
  border-radius: 10px;
  padding: 0 18px;
  height: 42px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover { background: #2f5bd8; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const TableCard = styled.section`
  background: #fff;
  border: 1px solid #e7eaf0;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  overflow: hidden;
`;

const TableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  text-align: ${props => props.align || 'left'};
  padding: 12px 20px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #98a2b3;
  background: #fbfbfd;
  border-bottom: 1px solid #eef0f4;
  white-space: nowrap;
`;

const Td = styled.td`
  text-align: ${props => props.align || 'left'};
  padding: 14px 20px;
  color: #1d2939;
  border-bottom: 1px solid #f4f6f9;
  white-space: nowrap;
  vertical-align: middle;
`;

const Row = styled.tr`
  cursor: pointer;
  transition: background 0.12s ease;

  &:hover { background: #f9fafe; }
`;

const ScopeName = styled.div`
  font-weight: 600;
  color: #101828;
`;

const ScopeMeta = styled.div`
  font-size: 12px;
  color: #98a2b3;
`;

const YearPill = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  font-weight: 700;
  color: #2f5bd8;
  background: #eaf0ff;
  border-radius: 999px;
  padding: 4px 12px;
`;

const Caret = styled.span`
  display: inline-block;
  width: 0;
  height: 0;
  margin-right: 10px;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid #98a2b3;
  transform: rotate(${props => (props.open ? '0deg' : '-90deg')});
  transition: transform 0.15s ease;
`;

const LinkBtn = styled.button`
  appearance: none;
  background: none;
  border: none;
  padding: 4px 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  color: ${props => (props.danger ? '#b42318' : '#2f5bd8')};

  &:hover { text-decoration: underline; }
`;

/* Expanded summary overview */
const SummaryCell = styled.td`
  padding: 0 20px 18px 46px;
  background: #fbfcfe;
  border-bottom: 1px solid #f4f6f9;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  padding-top: 14px;
`;

const SummaryBlock = styled.div`
  border: 1px solid #eef0f4;
  border-radius: 12px;
  padding: 14px 16px;
  background: #fff;
`;

const SummaryHeading = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #98a2b3;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SummaryTotal = styled.span`
  color: #101828;
`;

const SummaryRowLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  color: #475467;
  padding: 5px 0;
`;

const SummaryVal = styled.span`
  font-weight: 700;
  color: #1d2939;
`;

const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
  color: #667085;
  font-size: 15px;
`;

const StateCard = styled.div`
  background: #fff;
  border: 1px solid #e7eaf0;
  border-radius: 16px;
  padding: 56px 24px;
  text-align: center;
`;

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */
function formatNumber(value) {
  return numeral(value || 0).format('0,0');
}

const scopeKey = plan => `${plan.enrollmentYearId}|${plan.administrativeDivisionId || 'branch'}`;

// Collapse the flat plan rows into one record per (year, scope) with summary totals.
function groupPlans(plans) {
  const groups = {};

  (plans || []).forEach((plan) => {
    const key = scopeKey(plan);
    if (!groups[key]) {
      groups[key] = {
        key,
        enrollmentYearId: plan.enrollmentYearId,
        enrollmentYear: plan.enrollmentYear,
        administrativeDivisionId: plan.administrativeDivisionId || null,
        administrativeDivisionName: plan.administrativeDivisionName || null,
        administrativeDivisionLevel: plan.administrativeDivisionLevel || null,
        targetCount: 0,
        households: { total: 0, new: 0, renewal: 0, indigent: 0, paying: 0 },
        members: 0,
      };
    }
    const group = groups[key];
    group.targetCount += 1;

    if (plan.beneficiaryType === 'member') {
      group.members += plan.targetCount;
    } else {
      group.households.total += plan.targetCount;
      if (plan.planType === 'new') group.households.new += plan.targetCount;
      if (plan.planType === 'renewal') group.households.renewal += plan.targetCount;
      if (plan.category === 'indigent') group.households.indigent += plan.targetCount;
      if (plan.category === 'paying') group.households.paying += plan.targetCount;
    }
  });

  return Object.values(groups).sort((a, b) => {
    if (b.enrollmentYear !== a.enrollmentYear) return (b.enrollmentYear || 0) - (a.enrollmentYear || 0);
    return (a.administrativeDivisionName || '').localeCompare(b.administrativeDivisionName || '');
  });
}

function scopeLabel(group) {
  if (!group.administrativeDivisionId) return 'Whole branch';
  return group.administrativeDivisionName || `Division #${group.administrativeDivisionId}`;
}

/* ------------------------------------------------------------------ *
 * Summary overview (expanded row)
 * ------------------------------------------------------------------ */
function RecordSummary({ group }) {
  const { households, members } = group;

  return (
    <SummaryGrid>
      <SummaryBlock>
        <SummaryHeading>
          Households
          <SummaryTotal>{formatNumber(households.total)}</SummaryTotal>
        </SummaryHeading>
        <SummaryRowLine><span>New</span><SummaryVal>{formatNumber(households.new)}</SummaryVal></SummaryRowLine>
        <SummaryRowLine><span>Renewal</span><SummaryVal>{formatNumber(households.renewal)}</SummaryVal></SummaryRowLine>
        <SummaryRowLine><span>Indigent</span><SummaryVal>{formatNumber(households.indigent)}</SummaryVal></SummaryRowLine>
        <SummaryRowLine><span>Paying</span><SummaryVal>{formatNumber(households.paying)}</SummaryVal></SummaryRowLine>
      </SummaryBlock>

      <SummaryBlock>
        <SummaryHeading>
          Members
          <SummaryTotal>{formatNumber(members)}</SummaryTotal>
        </SummaryHeading>
        <SummaryRowLine><span>Total member target</span><SummaryVal>{formatNumber(members)}</SummaryVal></SummaryRowLine>
      </SummaryBlock>
    </SummaryGrid>
  );
}

RecordSummary.propTypes = {
  group: PropTypes.object.isRequired,
};

/* ------------------------------------------------------------------ *
 * View
 * ------------------------------------------------------------------ */
function EnrollmentPlansView({ user }) {
  const branchId = user && user.branchId;

  const [plans, setPlans] = useState([]);
  const [years, setYears] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedKey, setExpandedKey] = useState(null);
  const [modal, setModal] = useState(null); // { mode: 'create' | 'edit', group? }

  const loadPlans = useCallback(() => {
    if (!branchId) return Promise.resolve();
    return api.fetchEnrollmentPlans({ branchId })
      .then((data) => { setPlans(data || []); })
      .catch((err) => { setError(readError(err) || 'Failed to load enrollment plans'); });
  }, [branchId]);

  useEffect(() => {
    let cancelled = false;
    if (!branchId) {
      setError('Your account is not linked to a branch.');
      setIsLoading(false);
      return undefined;
    }

    setIsLoading(true);
    Promise.all([
      api.fetchEnrollmentYears(),
      api.fetchAdministrativeDivisions(branchId),
      api.fetchEnrollmentPlans({ branchId }),
    ])
      .then(([yearData, divisionData, planData]) => {
        if (cancelled) return;
        setYears(yearData || []);
        setDivisions((divisionData || []).filter(d => d.level !== 'gote'));
        setPlans(planData || []);
      })
      .catch((err) => {
        if (!cancelled) setError(readError(err) || 'Failed to load enrollment plans');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [branchId]);

  const groups = useMemo(() => groupPlans(plans), [plans]);

  const handleSaved = () => {
    setModal(null);
    loadPlans();
  };

  const handleDelete = (group) => {
    /* eslint-disable no-alert */
    const label = `${scopeLabel(group)} • ${group.enrollmentYear}`;
    if (!window.confirm(`Delete all enrollment targets for ${label}? This cannot be undone.`)) return;
    /* eslint-enable no-alert */

    // Deleting a scope = upserting every combination to zero.
    const emptyPlans = [
      { beneficiaryType: 'member', targetCount: 0 },
      ...['new', 'renewal'].flatMap(planType => ['indigent', 'paying'].map(category => ({
        planType, beneficiaryType: 'household', category, targetCount: 0,
      }))),
    ];

    api.saveEnrollmentPlans({
      enrollmentYearId: group.enrollmentYearId,
      branchId,
      administrativeDivisionId: group.administrativeDivisionId || undefined,
      plans: emptyPlans,
    })
      .then(loadPlans)
      .catch((err) => { setError(readError(err) || 'Failed to delete plan'); });
  };

  const branchName = (user && user.branchName) || 'Your branch';

  return (
    <>
      <Head title="Enrollment Plans" />
      <Menu supportsMobile />
      <Page>
        <Content>
          <HeaderCard>
            <div>
              <HeaderTitle>Enrollment Plans</HeaderTitle>
              <HeaderSubtitle>
                Set planned enrollment targets for each year and administrative division.
              </HeaderSubtitle>
            </div>
            <PrimaryBtn
              type="button"
              onClick={() => setModal({ mode: 'create' })}
              disabled={isLoading || !branchId}
            >
              + New plan
            </PrimaryBtn>
          </HeaderCard>

          {isLoading && (
            <StateCard><LoadingIndicator noun="enrollment plans" /></StateCard>
          )}

          {!isLoading && error && (
            <Box marginBottom={4}><Alert>{error}</Alert></Box>
          )}

          {!isLoading && !error && (
            <TableCard>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>Scope</Th>
                      <Th>Enrollment year</Th>
                      <Th align="right">Households target</Th>
                      <Th align="right">Members target</Th>
                      <Th align="right">Targets set</Th>
                      <Th align="right">Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.length === 0 ? (
                      <tr>
                        <Td colSpan={6}>
                          <EmptyState>
                            No enrollment plans yet. Use “New plan” to set targets for a year and scope.
                          </EmptyState>
                        </Td>
                      </tr>
                    ) : (
                      groups.map((group) => {
                        const open = expandedKey === group.key;
                        return (
                          <React.Fragment key={group.key}>
                            <Row onClick={() => setExpandedKey(open ? null : group.key)}>
                              <Td>
                                <Caret open={open} />
                                <ScopeName style={{ display: 'inline-block' }}>{scopeLabel(group)}</ScopeName>
                                {group.administrativeDivisionLevel ? (
                                  <ScopeMeta>{group.administrativeDivisionLevel}</ScopeMeta>
                                ) : null}
                              </Td>
                              <Td><YearPill>{group.enrollmentYear}</YearPill></Td>
                              <Td align="right">{formatNumber(group.households.total)}</Td>
                              <Td align="right">{formatNumber(group.members)}</Td>
                              <Td align="right">{group.targetCount}</Td>
                              <Td align="right" onClick={e => e.stopPropagation()}>
                                <LinkBtn type="button" onClick={() => setModal({ mode: 'edit', group })}>Edit</LinkBtn>
                                <LinkBtn type="button" danger onClick={() => handleDelete(group)}>Delete</LinkBtn>
                              </Td>
                            </Row>
                            {open && (
                              <tr>
                                <SummaryCell colSpan={6}>
                                  <RecordSummary group={group} />
                                </SummaryCell>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </TableWrap>
            </TableCard>
          )}
        </Content>
      </Page>

      {modal && (
        <EnrollmentPlanFormModal
          mode={modal.mode}
          branchId={branchId}
          branchName={branchName}
          years={years}
          divisions={divisions}
          group={modal.group}
          existingPlans={plans}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}

function readError(err) {
  return err
    && err.response
    && err.response.data
    && err.response.data.errors
    && err.response.data.errors.message;
}

EnrollmentPlansView.propTypes = {
  user: PropTypes.shape({
    branchId: PropTypes.number,
    branchName: PropTypes.string,
  }),
};

EnrollmentPlansView.defaultProps = { user: null };

export default connect(state => ({
  user: userSelector(state),
}))(EnrollmentPlansView);
